import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import TablePagination from "@components/TablePagination/TablePagination";

import { useNotification } from "../../context";

import { NotificationTabKey } from "../../model";

import { COLUMNS } from "./table.header";
import ActionsCell from "./ActionsCell";

import {
  INotification,
  INotificationWithUsers,
} from "@modules/notifications/model/INotifications";
import ROUTES from "@config/routes";

import ReadConfirmationModal from "../modal/ReadConfirmationModal";
import DeleteModal from "../modal/DeleteModal";
import { NotificationsSkeletonTable } from "@components/NotificationsSkeletonTable/NotificationsSkeletonTable";

interface Props {
  currentTab: NotificationTabKey;
}

const NotificationsTable: React.FC<Props> = ({ currentTab }) => {
  const history = useHistory();

  const {
    data,
    actions: { search, softDelete },
    filters,
    loading,
  } = useNotification();

  const [deleteTarget, setDeleteTarget] = useState<INotification | null>(null);
  const [readTarget, setReadTarget] = useState<INotificationWithUsers | null>(
    null
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    search({ page: newPage + 1, retrieveUsers: true });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    search({
      size: parseInt(event.target.value, 10),
      page: 1,
      retrieveUsers: true,
    });
  };

  const handleView = (notification: INotification) => {
    history.push(
      `${ROUTES.ADMIN_ROUTES.NOTIFICATIONS.VIEW}/${notification.id}`
    );
  };

  const handleEdit = (notification: INotification) => {
    history.push(
      `${ROUTES.ADMIN_ROUTES.NOTIFICATIONS.EDIT}/${notification.id}`
    );
  };

  const handleDelete = (notification: INotification) => {
    setDeleteTarget(notification);
  };

  const visibleColumns = COLUMNS.filter(
    (col) => !col.showInTabs || col.showInTabs.includes(currentTab)
  );

  if (loading) {
    return <NotificationsSkeletonTable visibleColumns={visibleColumns} />;
  }

  if (!data || data.rows.length === 0) {
    return <Typography>Nenhuma notificação encontrada.</Typography>;
  }

  return (
    <React.Fragment>
      <Table sx={{ borderCollapse: "separate", borderSpacing: "0 16px" }}>
        <TableHead sx={{ my: 10 }}>
          <TableRow sx={{ backgroundColor: "#FFF" }}>
            {visibleColumns.map((col) => (
              <TableCell
                key={col.id}
                sx={{
                  padding: "12px 16px",
                  minWidth: col.minWidth ?? "auto",
                  maxWidth: col.maxWidth ?? "auto",
                  textAlign: col.align ?? "left",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {col.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((notification) => {
            return (
              <TableRow key={notification.id} sx={{ backgroundColor: "#FFF" }}>
                {visibleColumns.map((col) => {
                  return (
                    <TableCell
                      key={col.id}
                      sx={{
                        minWidth: col.minWidth ?? "auto",
                        maxWidth: col.maxWidth ?? "auto",
                        align: col.align ?? "left",
                      }}
                    >
                      <Typography variant="body1">
                        {col.getValue?.(notification)}
                      </Typography>
                      {col.isActions && (
                        <ActionsCell
                          align={col.align ?? "left"}
                          currentTab={currentTab}
                          notification={notification}
                          onView={handleView}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onOpenModalReadConfirmation={setReadTarget}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={data.count}
        page={(filters.page || 1) - 1} // 0-based
        onPageChange={handleChangePage}
        rowsPerPage={filters.size || 10}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {deleteTarget && (
        <DeleteModal
          notificationId={deleteTarget.id}
          onClose={() => setDeleteTarget(null)}
          onDelete={softDelete}
        />
      )}

      {readTarget && (
        <ReadConfirmationModal
          notification={readTarget}
          onClose={() => setReadTarget(null)}
        />
      )}
    </React.Fragment>
  );
};

export default NotificationsTable;
